import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import AppointmentRequestWidget from '../../components/calendar/AppointmentRequestWidget';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Calendar, 
  Clock, 
  User, 
  Video, 
  Phone, 
  Building2, 
  CheckCircle2, 
  XCircle, 
  RefreshCw,
  Loader2,
  AlertCircle
} from 'lucide-react';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const ClientAppointmentsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [accountManager, setAccountManager] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingAppointments, setPendingAppointments] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [processing, setProcessing] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [managerRes, pendingRes, allRes] = await Promise.all([
        api.get('/users/my-manager').catch(() => ({ data: null })),
        api.get('/calendar/appointments/pending-validation').catch(() => ({ data: [] })),
        api.get('/calendar/appointments').catch(() => ({ data: [] }))
      ]);
      
      setAccountManager(managerRes.data);
      setPendingAppointments(pendingRes.data || []);
      setAllAppointments(allRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAccept = async (appointmentId) => {
    setProcessing(appointmentId);
    try {
      await api.post(`/calendar/appointments/${appointmentId}/client-accept`);
      toast.success('RDV confirmé !');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la confirmation');
    } finally {
      setProcessing(null);
    }
  };

  const handleDecline = async () => {
    if (!selectedAppointment) return;
    
    setProcessing(selectedAppointment.id);
    try {
      await api.post(`/calendar/appointments/${selectedAppointment.id}/client-decline`, {
        reason: declineReason
      });
      toast.success('RDV refusé');
      setShowDeclineModal(false);
      setDeclineReason('');
      setSelectedAppointment(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du refus');
    } finally {
      setProcessing(null);
    }
  };

  const handleCancel = async () => {
    if (!selectedAppointment) return;
    
    setProcessing(selectedAppointment.id);
    try {
      await api.post(`/calendar/appointments/${selectedAppointment.id}/client-cancel`, {
        reason: cancelReason
      });
      toast.success('RDV annulé avec succès');
      setShowCancelModal(false);
      setCancelReason('');
      setSelectedAppointment(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    } finally {
      setProcessing(null);
    }
  };

  const fetchAvailableSlots = async (appointment) => {
    setLoadingSlots(true);
    try {
      const res = await api.get(`/calendar/appointments/${appointment.id}/member-slots`);
      setAvailableSlots(res.data?.slots || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const openRescheduleModal = async (appointment) => {
    setSelectedAppointment(appointment);
    setShowRescheduleModal(true);
    await fetchAvailableSlots(appointment);
  };

  const handleReschedule = async () => {
    if (!selectedAppointment || !selectedSlot) return;
    
    const slot = availableSlots.find(s => `${s.date}-${s.time}` === selectedSlot);
    if (!slot) return;
    
    setProcessing(selectedAppointment.id);
    try {
      await api.post(`/calendar/appointments/${selectedAppointment.id}/client-reschedule`, {
        new_date: slot.date,
        new_time: slot.time,
        reason: rescheduleReason
      });
      toast.success('Demande de replanification envoyée');
      setShowRescheduleModal(false);
      setSelectedSlot('');
      setRescheduleReason('');
      setSelectedAppointment(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la demande');
    } finally {
      setProcessing(null);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'agency': return <Building2 className="w-4 h-4" />;
      case 'call': return <Video className="w-4 h-4" />;
      case 'phone': return <Phone className="w-4 h-4" />;
      default: return <Calendar className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'agency': return 'En agence';
      case 'call': return 'Visioconférence';
      case 'phone': return 'Téléphone';
      default: return 'RDV';
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      'pending_client': { label: 'À valider', className: 'bg-amber-100 text-amber-700' },
      'pending': { label: 'En attente', className: 'bg-blue-100 text-blue-700' },
      'accepted': { label: 'Confirmé', className: 'bg-green-100 text-green-700' },
      'declined': { label: 'Refusé', className: 'bg-red-100 text-red-700' },
      'pending_reschedule': { label: 'Replanification demandée', className: 'bg-purple-100 text-purple-700' },
      'cancelled': { label: 'Annulé', className: 'bg-slate-100 text-slate-700' },
      'completed': { label: 'Terminé', className: 'bg-slate-100 text-slate-700' }
    };
    const c = config[status] || { label: status, className: 'bg-slate-100 text-slate-700' };
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className={cn(
        "transition-all duration-300",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className="p-4 md:p-8" style={{ paddingTop: '140px' }}>
          <div className="max-w-4xl mx-auto space-y-6">
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Mes Rendez-vous
            </h1>
            
            {/* RDV en attente de validation */}
            {pendingAppointments.length > 0 && (
              <Card className="border-2 border-amber-300 bg-amber-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-700">
                    <AlertCircle className="w-5 h-5" />
                    Rendez-vous à confirmer ({pendingAppointments.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {pendingAppointments.map((apt) => (
                    <div key={apt.id} className="p-4 bg-white rounded-lg border shadow-sm">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            {getTypeIcon(apt.appointment_type)}
                            <span className="font-semibold">{apt.subject}</span>
                            <Badge variant="outline">{getTypeLabel(apt.appointment_type)}</Badge>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(apt.requested_date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {apt.requested_time} ({apt.duration_minutes} min)
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {apt.account_manager_name}
                            </span>
                          </div>
                          {apt.description && (
                            <p className="text-sm text-slate-500">{apt.description}</p>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          <Button
                            onClick={() => handleAccept(apt.id)}
                            disabled={processing === apt.id}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {processing === apt.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                            )}
                            Accepter
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => openRescheduleModal(apt)}
                            disabled={processing === apt.id}
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Replanifier
                          </Button>
                          <Button
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => {
                              setSelectedAppointment(apt);
                              setShowDeclineModal(true);
                            }}
                            disabled={processing === apt.id}
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Refuser
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Tous mes RDV */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Historique de mes rendez-vous
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : allAppointments.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Aucun rendez-vous</p>
                ) : (
                  <div className="space-y-3">
                    {allAppointments.filter(a => a.status !== 'pending_client').map((apt) => (
                      <div key={apt.id} className="p-3 bg-slate-50 rounded-lg border flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            {getTypeIcon(apt.appointment_type)}
                            <span className="font-medium">{apt.subject}</span>
                            {getStatusBadge(apt.status)}
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                            <span>{formatDate(apt.requested_date)} à {apt.requested_time}</span>
                            <span>avec {apt.account_manager_name}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {apt.meeting_link && apt.status === 'accepted' && (
                            <Button size="sm" asChild>
                              <a href={apt.meeting_link} target="_blank" rel="noopener noreferrer">
                                <Video className="w-4 h-4 mr-2" />
                                Rejoindre
                              </a>
                            </Button>
                          )}
                          {/* Boutons pour RDV confirmés */}
                          {apt.status === 'accepted' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openRescheduleModal(apt)}
                                disabled={processing === apt.id}
                              >
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Reprogrammer
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => {
                                  setSelectedAppointment(apt);
                                  setShowCancelModal(true);
                                }}
                                disabled={processing === apt.id}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Annuler
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Widget de demande de RDV */}
            <AppointmentRequestWidget 
              theme={theme} 
              user={user} 
              accountManager={accountManager}
            />
          </div>
        </main>
      </div>

      {/* Modal Refuser */}
      <Dialog open={showDeclineModal} onOpenChange={setShowDeclineModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Refuser le rendez-vous
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Vous êtes sur le point de refuser le RDV &quot;{selectedAppointment?.subject}&quot; 
              du {formatDate(selectedAppointment?.requested_date)} à {selectedAppointment?.requested_time}.
            </p>
            <label className="text-sm font-medium">Motif (optionnel)</label>
            <Textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Indiquez le motif du refus..."
              className="mt-1"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeclineModal(false)}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDecline}
              disabled={processing}
            >
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Refuser le RDV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Replanifier */}
      <Dialog open={showRescheduleModal} onOpenChange={setShowRescheduleModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              Proposer un autre créneau
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-600">
              Choisissez un créneau parmi les disponibilités de {selectedAppointment?.account_manager_name} :
            </p>
            
            {loadingSlots ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : availableSlots.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">
                Aucun créneau disponible pour le moment
              </p>
            ) : (
              <Select value={selectedSlot} onValueChange={setSelectedSlot}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un créneau..." />
                </SelectTrigger>
                <SelectContent>
                  {availableSlots.map((slot) => (
                    <SelectItem key={`${slot.date}-${slot.time}`} value={`${slot.date}-${slot.time}`}>
                      {slot.formatted}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            <div>
              <label className="text-sm font-medium">Motif (optionnel)</label>
              <Textarea
                value={rescheduleReason}
                onChange={(e) => setRescheduleReason(e.target.value)}
                placeholder="Pourquoi ce créneau vous convient mieux..."
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRescheduleModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleReschedule}
              disabled={!selectedSlot || processing}
              style={{ backgroundColor: theme.colors?.accent }}
            >
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Envoyer la demande
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Annulation */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Annuler le rendez-vous
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Vous êtes sur le point d&apos;annuler le RDV &quot;{selectedAppointment?.subject}&quot; 
              du {formatDate(selectedAppointment?.requested_date)} à {selectedAppointment?.requested_time}.
            </p>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
              <p className="text-sm text-amber-700">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Cette action est irréversible. Le responsable de compte sera notifié.
              </p>
            </div>
            <label className="text-sm font-medium">Motif de l&apos;annulation (optionnel)</label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Indiquez le motif de l'annulation..."
              className="mt-1"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelModal(false)}>
              Retour
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancel}
              disabled={processing}
            >
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmer l&apos;annulation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientAppointmentsPage;
